import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { FeatureFlags } from '@lynx-crypto/contracts/feature-flags';
import { useMaintenanceStore } from '../../stores/maintenanceStore';
import { useFeatureFlagStore } from '../../stores/featureFlagStore';

/**
 * Blocks ALL routes until the first admin account is created.
 * Checks /auth/status on mount — if needsSetup is true, redirects to /setup.
 * Also populates the maintenance store AND the feature-flag store from the same
 * response (the public status payload carries the complete feature-flag map).
 * Once setup is complete (or if auth-service is unreachable), renders children.
 */
export function SetupGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const setMaintenanceEnabled = useMaintenanceStore((s) => s.setEnabled);
  const setFeatureFlags = useFeatureFlagStore((s) => s.setFlags);
  const [status, setStatus] = useState<'checking' | 'needs-setup' | 'ready'>(
    'checking',
  );

  useEffect(() => {
    let cancelled = false;
    fetch('/auth/status')
      .then((r) => r.json())
      .then(
        (d: {
          needsSetup?: boolean;
          maintenanceMode?: boolean;
          featureFlags?: FeatureFlags;
        }) => {
          if (cancelled) return;
          setMaintenanceEnabled(d.maintenanceMode ?? false);
          if (d.featureFlags) setFeatureFlags(d.featureFlags);
          setStatus(d.needsSetup ? 'needs-setup' : 'ready');
        },
      )
      .catch(() => {
        // Auth-service unreachable — don't block the app
        if (!cancelled) setStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, setMaintenanceEnabled, setFeatureFlags]);

  if (status === 'checking') return null;
  if (status === 'needs-setup') return <Navigate to="/setup" replace />;
  return <>{children}</>;
}
